import React, { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
    setLoadingFalse,
    setLoadingTrue,
} from "../../../src/Config/Actions/loadingActions";
import {
    apiCallCreateSubscriptionCreatePlan,
    apiCallCreateSubscriptionEditPlan,
} from "../../../src/Core/Apicall";
import { CustomUploadProfilePic } from "../../../src/Core/CustomUploadProfilePic";
import { toast } from "react-toastify";

// Constants
export const ArtRunner = "ArtRunner";
export const ArtLover = "ArtLover";
export const Artist = "Artist";
export const ArtistPro = "ArtistPro";

const PLAN_TYPES = {
    FREE: "FREE",
    PRO: "PRO",
    AMBASSADOR: "AMBASSADOR"
};

const PLAN_TYPE_CONFIG = {
    [PLAN_TYPES.FREE]: {
        label: "Free",
        icon: "🆓",
        color: "#4CAF50",
        bgColor: "rgba(76, 175, 80, 0.12)",
        borderColor: "rgba(76, 175, 80, 0.3)",
        description: "Default plan - Always active",
        showFeatures: false,
        showBilling: false,
        showAmbassadorFields: false,
        showProFields: false,
        showAchievements: false,
        isDefaultActive: true,
    },
    [PLAN_TYPES.PRO]: {
        label: "Pro",
        icon: "⭐",
        color: "#FF6B35",
        bgColor: "rgba(255, 107, 53, 0.12)",
        borderColor: "rgba(255, 107, 53, 0.3)",
        description: "Paid subscription via IAP",
        showFeatures: false,
        showBilling: true,
        showAmbassadorFields: false,
        showProFields: true,
        showAchievements: false,
        isDefaultActive: false,
    },
    [PLAN_TYPES.AMBASSADOR]: {
        label: "Ambassador",
        icon: "👑",
        color: "#9C27B0",
        bgColor: "rgba(156, 39, 176, 0.12)",
        borderColor: "rgba(156, 39, 176, 0.3)",
        description: "Achievement-based free access",
        showFeatures: false,
        showBilling: false,
        showAmbassadorFields: true,
        showProFields: false,
        showAchievements: true,
        isDefaultActive: false,
    }
};

const BILLING_PERIODS = [
    { label: "Monthly", value: "MONTHLY", days: 30, icon: "📆" },
    { label: "Quarterly", value: "QUARTERLY", days: 90, icon: "📊" },
    { label: "Half Yearly", value: "HALF_YEARLY", days: 182, icon: "📈" },
    { label: "Yearly", value: "YEARLY", days: 365, icon: "📅" }
];

const USER_TYPES = [ArtRunner, ArtLover, Artist, ArtistPro];

// ✅ Achievement options for Ambassador - Keys matching backend
const ACHIEVEMENT_OPTIONS = [
    { key: "check_ins", label: "Check-ins", icon: "✅", defaultCount: 30 },
    { key: "media_uploads", label: "Media Uploads", icon: "🎨", defaultCount: 50 },
    { key: "referrals", label: "Referrals", icon: "👥", defaultCount: 10 },
];

const CreatePlanModal = ({ openModal, onClose, editData, onSuccess }) => {
    const dispatch = useDispatch();

    // State
    const [uploadImage, setUploadImage] = useState(true);
    const [imagePreview, setImagePreview] = useState("");
    const [isUploading, setIsUploading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const [touchedFields, setTouchedFields] = useState({});

    const [formData, setFormData] = useState({
        title: "",
        description: "",
        order: null,
        type: "",
        userType: "",
        productId: "",
        pricing: {
            price: "",
            interval: "MONTHLY",
            currency: "USD"
        },
        ambassadorDuration: "MONTHLY",
        achievementRequirements: {},
        tag: "",
        isDefaultActive: false,
    });

    // Effects - Populate edit data
    useEffect(() => {
        if (editData) {
            console.log("📝 Editing plan:", editData);

            const pricing = editData.pricing || {};
            const ambassadorDuration = editData.ambassadorDuration || "MONTHLY";
            const achievementRequirements = editData.achievementRequirements || {};

            setFormData({
                title: editData.title || "",
                description: editData.description || "",
                order: editData.order || "",
                type: editData.type || "",
                userType: editData.userType || "",
                productId: editData.productId || "",
                pricing: {
                    price: pricing.price || "",
                    interval: pricing.interval || "MONTHLY",
                    currency: pricing.currency || "USD"
                },
                ambassadorDuration: ambassadorDuration,
                achievementRequirements: achievementRequirements,
                tag: editData.tag || "",
                isDefaultActive: editData.isDefaultActive || false,
            });

            setImagePreview(editData.icon || "");
            setUploadImage(!editData.icon);
        }
    }, [editData]);

    // Reset fields based on plan type
    useEffect(() => {
        if (formData.type === PLAN_TYPES.FREE) {
            setFormData(prev => ({
                ...prev,
                productId: "",
                pricing: { price: "", interval: "MONTHLY", currency: "USD" },
                isDefaultActive: true,
                achievementRequirements: {},
                userType: "",
            }));
        } else if (formData.type === PLAN_TYPES.PRO) {
            setFormData(prev => ({
                ...prev,
                productId: prev.productId || "",
                isDefaultActive: false,
                achievementRequirements: {},
            }));
        } else if (formData.type === PLAN_TYPES.AMBASSADOR) {
            setFormData(prev => ({
                ...prev,
                productId: "",
                pricing: { interval: prev.ambassadorDuration || "MONTHLY", currency: "USD" },
                isDefaultActive: false,
            }));
        }
    }, [formData.type]);

    // Validation
    const validateForm = useCallback(() => {
        const errors = {};

        if (!formData.title?.trim()) {
            errors.title = "Plan title is required";
        }

        if (!formData.type) {
            errors.type = "Please select a plan type";
        }

        if (!formData.description?.trim()) {
            errors.description = "Description is required";
        }

        if (formData.order === null || formData.order === "" || formData.order < 0) {
            errors.order = "Valid order number is required";
        }

        if (!imagePreview) {
            errors.icon = "Plan icon is required";
        }

        // PRO plan validation
        if (formData.type === PLAN_TYPES.PRO) {
            if (!formData.productId?.trim()) {
                errors.productId = "Product ID is required";
            }
            if (!formData.pricing.price || Number(formData.pricing.price) <= 0) {
                errors.price = "Valid price is required";
            }
            if (!formData.userType) {
                errors.userType = "User type is required";
            }
        }

        // AMBASSADOR validation
        if (formData.type === PLAN_TYPES.AMBASSADOR) {
            if (!formData.ambassadorDuration) {
                errors.ambassadorDuration = "Duration is required";
            }
            if (!formData.userType) {
                errors.userType = "User type is required";
            }
            // Validate achievement requirements
            if (Object.keys(formData.achievementRequirements).length === 0) {
                errors.achievements = "Add at least one achievement requirement";
            }
        }

        // FREE plan validation
        if (formData.type === PLAN_TYPES.FREE) {
            if (!formData.userType) {
                errors.userType = "User type is required";
            }
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    }, [formData, imagePreview]);

    // Handlers
    const handleSubmit = async (e) => {
        e.preventDefault();

        setTouchedFields({
            title: true,
            description: true,
            type: true,
            order: true,
            ...(formData.type === PLAN_TYPES.PRO && {
                productId: true,
                price: true,
                userType: true,
            }),
            ...(formData.type === PLAN_TYPES.AMBASSADOR && {
                ambassadorDuration: true,
                achievements: true,
                userType: true,
            }),
            ...(formData.type === PLAN_TYPES.FREE && {
                userType: true,
            })
        });

        if (!validateForm()) {
            const firstError = document.querySelector(".error-message");
            if (firstError) {
                firstError.scrollIntoView({ behavior: "smooth", block: "center" });
            }
            return;
        }

        setIsSubmitting(true);
        dispatch(setLoadingTrue());

        const param = {
            sub_id: editData?.sub_id || "plan_" + Date.now(),
            type: formData.type,
            title: formData.title,
            userType: formData.userType,
            description: formData.description,
            order: Number(formData.order),
            productId: formData.type === PLAN_TYPES.PRO ? formData.productId : "",
            pricing: formData.type === PLAN_TYPES.PRO
                ? {
                    price: Number(formData.pricing.price),
                    currency: "USD",
                    interval: formData.pricing.interval
                }
                : formData.type === PLAN_TYPES.AMBASSADOR
                    ? { interval: formData.ambassadorDuration }
                    : formData.type === PLAN_TYPES.FREE
                        ? { interval: "MONTHLY" }
                        : undefined,
            icon: imagePreview,
            tag: formData.tag,
            isDefaultActive: formData.type === PLAN_TYPES.FREE ? true : false,
            ambassadorDuration: formData.type === PLAN_TYPES.AMBASSADOR ? formData.ambassadorDuration : undefined,
            achievementRequirements: formData.type === PLAN_TYPES.AMBASSADOR ? formData.achievementRequirements : {},
        };

        console.log("📤 Submitting plan:", param);

        try {
            if (editData) {
                await apiCallCreateSubscriptionEditPlan({ planId: editData._id, ...param });
                toast.success("✨ Plan updated successfully");
            } else {
                await apiCallCreateSubscriptionCreatePlan(param);
                toast.success("✨ Plan created successfully");
            }
            onSuccess();
            handleClose();
        } catch (error) {
            console.error("❌ Submit error:", error);
            toast.error(error.message || "Something went wrong");
        } finally {
            setIsSubmitting(false);
            dispatch(setLoadingFalse());
        }
    };

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const validTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
        if (!validTypes.includes(file.type)) {
            toast.error("Please upload PNG, JPEG, WEBP, or SVG image");
            return;
        }

        if (file.size > 2 * 1024 * 1024) {
            toast.error("Image size should be less than 2MB");
            return;
        }

        setIsUploading(true);
        dispatch(setLoadingTrue());

        const formData = new FormData();
        formData.append("imageURL", file);
        formData.append("imageType", "PlanIcons");
        formData.append("imageStoreId", `plan_${Date.now()}`);

        try {
            const data = await CustomUploadProfilePic(formData);
            setImagePreview(data.location);
            setUploadImage(false);
            toast.success("Icon uploaded successfully");
        } catch (error) {
            toast.error("Failed to upload icon");
        } finally {
            setIsUploading(false);
            dispatch(setLoadingFalse());
        }
    };

    const resetForm = () => {
        setFormData({
            title: "",
            description: "",
            order: null,
            type: "",
            userType: "",
            productId: "",
            pricing: { price: "", interval: "MONTHLY", currency: "USD" },
            ambassadorDuration: "MONTHLY",
            achievementRequirements: {},
            tag: "",
            isDefaultActive: false,
        });
        setImagePreview("");
        setUploadImage(true);
        setFormErrors({});
        setTouchedFields({});
    };

    const handleClose = () => {
        resetForm();
        if (typeof onClose === "function") onClose(false);
    };

    const handleImageDelete = () => {
        setImagePreview("");
        setUploadImage(true);
    };

    // Achievement handlers
    const toggleAchievement = (key) => {
        setFormData(prev => {
            const newAchievements = { ...prev.achievementRequirements };
            if (newAchievements[key]) {
                delete newAchievements[key];
            } else {
                const option = ACHIEVEMENT_OPTIONS.find(opt => opt.key === key);
                newAchievements[key] = {
                    title: option.label,
                    description: `Complete ${option.defaultCount} ${option.label.toLowerCase()}`,
                    requiredCount: option.defaultCount,
                    icon: option.icon
                };
            }
            return { ...prev, achievementRequirements: newAchievements };
        });
    };

    const updateAchievement = (key, field, value) => {
        setFormData(prev => ({
            ...prev,
            achievementRequirements: {
                ...prev.achievementRequirements,
                [key]: {
                    ...prev.achievementRequirements[key],
                    [field]: value
                }
            }
        }));
    };

    // Computed values
    const showBilling = formData.type === PLAN_TYPES.PRO;
    const showAmbassadorFields = formData.type === PLAN_TYPES.AMBASSADOR;
    const showProFields = formData.type === PLAN_TYPES.PRO;
    const showFreeNote = formData.type === PLAN_TYPES.FREE;
    const showAchievements = formData.type === PLAN_TYPES.AMBASSADOR;
    const selectedPlanConfig = formData.type ? PLAN_TYPE_CONFIG[formData.type] : null;
    const selectedPeriod = BILLING_PERIODS.find(p => p.value === formData.pricing.interval);

    if (!openModal) return null;

    return (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && handleClose()}>
            <div className="modal-container">
                {/* Close Button */}
                <button className="close-btn" onClick={handleClose}>
                    ✕
                </button>

                {/* Header */}
                <div className="modal-header">
                    <div className="header-icon-wrapper">
                        <div className="header-icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                            </svg>
                        </div>
                    </div>
                    <h2>{editData ? "Edit Plan" : "Create New Plan"}</h2>
                    <p className="subtitle">Configure your subscription plan details</p>
                </div>

                {/* Plan Type Selection */}
                <div className="plan-type-section">
                    <label className="section-label">
                        Plan Type <span className="required">*</span>
                    </label>
                    <div className="plan-type-grid">
                        {Object.values(PLAN_TYPES).map((type) => {
                            const config = PLAN_TYPE_CONFIG[type];
                            const isActive = formData.type === type;
                            return (
                                <button
                                    key={type}
                                    className={`plan-type-card ${isActive ? 'active' : ''}`}
                                    onClick={() => {
                                        setFormData(prev => ({ ...prev, type }));
                                        setTouchedFields(prev => ({ ...prev, type: true }));
                                    }}
                                    style={{
                                        borderColor: isActive ? config.color : 'rgba(255,255,255,0.08)',
                                        background: isActive ? config.bgColor : 'transparent'
                                    }}
                                >
                                    <div className="plan-type-icon" style={{ color: config.color }}>
                                        {config.icon}
                                    </div>
                                    <div className="plan-type-info">
                                        <span className="plan-type-name" style={{ color: isActive ? config.color : '#e8edf5' }}>
                                            {config.label}
                                        </span>
                                        <span className="plan-type-badge">{config.description}</span>
                                    </div>
                                    {isActive && (
                                        <div className="plan-type-check">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={config.color} strokeWidth="3">
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                    {touchedFields.type && formErrors.type && (
                        <span className="error-message">{formErrors.type}</span>
                    )}
                </div>

                {/* Info Banner */}
                {selectedPlanConfig && (
                    <div className="info-banner" style={{
                        borderColor: selectedPlanConfig.borderColor,
                        background: selectedPlanConfig.bgColor
                    }}>
                        <div className="info-icon" style={{ color: selectedPlanConfig.color }}>
                            {selectedPlanConfig.icon}
                        </div>
                        <div className="info-content">
                            <strong style={{ color: selectedPlanConfig.color }}>
                                {selectedPlanConfig.label} Plan Configuration
                            </strong>
                            <p>
                                {formData.type === PLAN_TYPES.FREE &&
                                    "Default plan that is always active for all users. User type selection is required."}
                                {formData.type === PLAN_TYPES.PRO &&
                                    "Paid subscription via IAP. User type and Product ID needed for payment integration."}
                                {formData.type === PLAN_TYPES.AMBASSADOR &&
                                    "Users complete achievements to unlock free access. User type and achievements are required."}
                            </p>
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    {/* User Type - Common for all plans */}
                    <div className="form-group">
                        <label className="field-label">
                            User Type <span className="required">*</span>
                            <span className="field-hint">Role-based access</span>
                        </label>
                        <div className="input-wrapper">
                            <select
                                value={formData.userType || ""}
                                onChange={(e) => {
                                    setFormData(prev => ({ ...prev, userType: e.target.value }));
                                    setTouchedFields(prev => ({ ...prev, userType: true }));
                                }}
                                onBlur={() => setTouchedFields(prev => ({ ...prev, userType: true }))}
                                className={`form-input ${touchedFields.userType && formErrors.userType ? 'error' : ''}`}
                                style={{ appearance: 'auto', cursor: 'pointer' }}
                            >
                                <option value="" style={{ background: '#141c2e', color: '#6b7a96' }}>
                                    Select User Type
                                </option>
                                {USER_TYPES.map((type) => (
                                    <option key={type} value={type} style={{ background: '#141c2e', color: '#e8edf5' }}>
                                        {type}
                                    </option>
                                ))}
                            </select>
                            {touchedFields.userType && formErrors.userType && (
                                <span className="error-message">{formErrors.userType}</span>
                            )}
                        </div>
                    </div>

                    {/* Title */}
                    <div className="form-group">
                        <label className="field-label">
                            Title <span className="required">*</span>
                        </label>
                        <div className="input-wrapper">
                            <input
                                type="text"
                                placeholder="e.g. Pro Monthly"
                                value={formData.title}
                                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                onBlur={() => setTouchedFields(prev => ({ ...prev, title: true }))}
                                className={`form-input ${touchedFields.title && formErrors.title ? 'error' : ''}`}
                            />
                            {touchedFields.title && formErrors.title && (
                                <span className="error-message">{formErrors.title}</span>
                            )}
                        </div>
                    </div>

                    {/* Description */}
                    <div className="form-group">
                        <label className="field-label">
                            Description <span className="required">*</span>
                        </label>
                        <div className="input-wrapper">
                            <textarea
                                placeholder="Short plan description"
                                value={formData.description}
                                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                onBlur={() => setTouchedFields(prev => ({ ...prev, description: true }))}
                                className={`form-input form-textarea ${touchedFields.description && formErrors.description ? 'error' : ''}`}
                                rows="2"
                            />
                            {touchedFields.description && formErrors.description && (
                                <span className="error-message">{formErrors.description}</span>
                            )}
                        </div>
                    </div>

                    {/* FREE Plan Note */}
                    {showFreeNote && (
                        <div className="free-note">
                            <div className="free-note-icon">ℹ️</div>
                            <div className="free-note-content">
                                <strong>Default Plan</strong>
                                <p>This plan will be automatically active for all users with the selected user type when they create an account.</p>
                            </div>
                        </div>
                    )}

                    {/* Ambassador Specific Fields */}
                    {showAmbassadorFields && (
                        <div className="ambassador-fields">
                            <div className="form-group">
                                <label className="field-label">
                                    Free Access Duration <span className="required">*</span>
                                </label>
                                <div className="ambassador-duration-grid">
                                    {BILLING_PERIODS.map((duration) => (
                                        <button
                                            key={duration.value}
                                            type="button"
                                            className={`ambassador-duration-card ${formData.ambassadorDuration === duration.value ? 'active' : ''}`}
                                            onClick={() => {
                                                setFormData(prev => ({ ...prev, ambassadorDuration: duration.value }));
                                                setTouchedFields(prev => ({ ...prev, ambassadorDuration: true }));
                                            }}
                                        >
                                            <span className="duration-icon">{duration.icon}</span>
                                            <span className="duration-label">{duration.label}</span>
                                            <span className="duration-days">FREE access</span>
                                        </button>
                                    ))}
                                </div>
                                {touchedFields.ambassadorDuration && formErrors.ambassadorDuration && (
                                    <span className="error-message">{formErrors.ambassadorDuration}</span>
                                )}
                            </div>
                        </div>
                    )}

                    {/* PRO Specific Fields */}
                    {showBilling && (
                        <div className="pro-fields">
                            <div className="form-group">
                                <label className="field-label">
                                    Product ID <span className="required">*</span>
                                    <span className="field-hint">For IAP integration</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="com.example.app.pro_monthly"
                                    value={formData.productId}
                                    onChange={(e) => setFormData(prev => ({ ...prev, productId: e.target.value }))}
                                    onBlur={() => setTouchedFields(prev => ({ ...prev, productId: true }))}
                                    className={`form-input ${touchedFields.productId && formErrors.productId ? 'error' : ''}`}
                                />
                                {touchedFields.productId && formErrors.productId && (
                                    <span className="error-message">{formErrors.productId}</span>
                                )}
                            </div>

                            <div className="form-group">
                                <label className="field-label">
                                    Billing Period <span className="required">*</span>
                                </label>
                                <div className="billing-period-grid">
                                    {BILLING_PERIODS.map((period) => (
                                        <button
                                            key={period.value}
                                            type="button"
                                            className={`billing-period-card ${formData.pricing.interval === period.value ? 'active' : ''}`}
                                            onClick={() => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    pricing: { ...prev.pricing, interval: period.value }
                                                }));
                                                setTouchedFields(prev => ({ ...prev, interval: true }));
                                            }}
                                        >
                                            <span className="period-icon">{period.icon}</span>
                                            <span className="period-label">{period.label}</span>
                                            <span className="period-days">{period.days} days</span>
                                        </button>
                                    ))}
                                </div>
                                {touchedFields.interval && formErrors.interval && (
                                    <span className="error-message">{formErrors.interval}</span>
                                )}
                            </div>

                            <div className="form-group">
                                <label className="field-label">
                                    Price (USD) <span className="required">*</span>
                                </label>
                                <div className="price-input-wrapper">
                                    <span className="currency-symbol">$</span>
                                    <input
                                        type="number"
                                        placeholder="0.00"
                                        value={formData.pricing.price}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            pricing: { ...prev.pricing, price: e.target.value }
                                        }))}
                                        onBlur={() => setTouchedFields(prev => ({ ...prev, price: true }))}
                                        className={`form-input price-input ${touchedFields.price && formErrors.price ? 'error' : ''}`}
                                        min="0"
                                        step="0.01"
                                    />
                                    {selectedPeriod && (
                                        <span className="period-label-display">/{selectedPeriod.label.toLowerCase()}</span>
                                    )}
                                </div>
                                {touchedFields.price && formErrors.price && (
                                    <span className="error-message">{formErrors.price}</span>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Ambassador Achievement Section */}
                    {showAchievements && (
                        <div className="achievements-section">
                            <label className="field-label">
                                Achievement Requirements <span className="required">*</span>
                                <span className="field-hint">Users must complete these to unlock ambassador</span>
                            </label>

                            <div className="achievement-grid">
                                {ACHIEVEMENT_OPTIONS.map((achievement) => {
                                    const isSelected = formData.achievementRequirements[achievement.key];
                                    const achData = formData.achievementRequirements[achievement.key] || {};

                                    return (
                                        <div key={achievement.key} className={`achievement-card ${isSelected ? 'active' : ''}`}>
                                            <div className="achievement-header">
                                                <div className="achievement-info">
                                                    <span className="achievement-icon">{achievement.icon}</span>
                                                    <span className="achievement-label">{achievement.label}</span>
                                                </div>
                                                <div
                                                    className={`toggle ${isSelected ? 'on' : 'off'}`}
                                                    onClick={() => toggleAchievement(achievement.key)}
                                                >
                                                    <div className={`toggle-thumb ${isSelected ? 'on' : 'off'}`} />
                                                </div>
                                            </div>

                                            {isSelected && (
                                                <div className="achievement-fields">
                                                    <div className="achievement-field">
                                                        <label className="achievement-field-label">Required Count</label>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            value={achData.requiredCount || achievement.defaultCount}
                                                            onChange={(e) => updateAchievement(achievement.key, 'requiredCount', Number(e.target.value))}
                                                            className="achievement-input"
                                                        />
                                                    </div>
                                                    <div className="achievement-field full-width">
                                                        <label className="achievement-field-label">Description</label>
                                                        <input
                                                            type="text"
                                                            placeholder="Describe the achievement requirement"
                                                            value={achData.description || `Complete ${achievement.defaultCount} ${achievement.label.toLowerCase()}`}
                                                            onChange={(e) => updateAchievement(achievement.key, 'description', e.target.value)}
                                                            className="achievement-input"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {formErrors.achievements && (
                                <span className="error-message">{formErrors.achievements}</span>
                            )}
                        </div>
                    )}

                    {/* Order & Icon Row */}
                    <div className="form-row">
                        <div className="form-group">
                            <label className="field-label">
                                Display Order <span className="required">*</span>
                            </label>
                            <input
                                type="number"
                                placeholder="1"
                                value={formData.order || ""}
                                onChange={(e) => setFormData(prev => ({ ...prev, order: e.target.value }))}
                                onBlur={() => setTouchedFields(prev => ({ ...prev, order: true }))}
                                className={`form-input ${touchedFields.order && formErrors.order ? 'error' : ''}`}
                                min="0"
                            />
                            {touchedFields.order && formErrors.order && (
                                <span className="error-message">{formErrors.order}</span>
                            )}
                        </div>
                        <div className="form-group">
                            <label className="field-label">
                                Plan Icon <span className="required">*</span>
                            </label>
                            <div className={`upload-zone ${formErrors.icon ? 'error' : ''}`}>
                                {uploadImage ? (
                                    <label className="upload-label">
                                        <div className="upload-icon">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                <polyline points="17 8 12 3 7 8" />
                                                <line x1="12" y1="3" x2="12" y2="15" />
                                            </svg>
                                        </div>
                                        <div className="upload-text">
                                            <span className="upload-main">{isUploading ? 'Uploading...' : 'Upload Icon'}</span>
                                            <span className="upload-sub">PNG, JPEG, WEBP, SVG</span>
                                        </div>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageUpload}
                                            disabled={isUploading}
                                            className="file-input"
                                        />
                                    </label>
                                ) : (
                                    <div className="image-preview-wrapper">
                                        <img src={imagePreview} alt="Plan icon" className="image-preview" />
                                        <button
                                            type="button"
                                            className="delete-image-btn"
                                            onClick={handleImageDelete}
                                            title="Remove icon"
                                        >
                                            ×
                                        </button>
                                    </div>
                                )}
                            </div>
                            {formErrors.icon && (
                                <span className="error-message">{formErrors.icon}</span>
                            )}
                        </div>
                    </div>

                    {/* Tag Section */}
                    <div className="form-group">
                        <label className="field-label">Tag</label>
                        <div className="tag-input-wrapper">
                            <div className="tag-input-header">
                                <span className="tag-icon">🏷️</span>
                                <span className="tag-label-text">Plan Tag</span>
                            </div>
                            <input
                                type="text"
                                placeholder="e.g. Most Popular, Best Value, Limited Offer"
                                value={formData.tag}
                                onChange={(e) => setFormData(prev => ({ ...prev, tag: e.target.value }))}
                                className="form-input tag-input"
                            />
                            <div className="tag-hint">
                                Add a custom tag to highlight your plan
                            </div>
                        </div>
                    </div>

                    {/* Submit */}
                    <button
                        type="submit"
                        className="submit-btn"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="spinner"></span>
                                {editData ? "Saving..." : "Creating..."}
                            </>
                        ) : (
                            <>
                                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                                {editData ? "Save Changes" : "Create Plan"}
                            </>
                        )}
                    </button>
                </form>
            </div>

            <style jsx>{`
                /* Modal Overlay */
                .modal-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0, 0, 0, 0.7);
                    backdrop-filter: blur(8px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: 20px;
                }

                .modal-container {
                    background: #141c2e;
                    border: 1px solid rgba(255, 255, 255, 0.06);
                    border-radius: 20px;
                    width: 100%;
                    max-width: 720px;
                    max-height: 90vh;
                    overflow-y: auto;
                    padding: 32px;
                    position: relative;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
                }

                .modal-container::-webkit-scrollbar {
                    width: 4px;
                }
                .modal-container::-webkit-scrollbar-track {
                    background: transparent;
                }
                .modal-container::-webkit-scrollbar-thumb {
                    background: rgba(61, 111, 255, 0.3);
                    border-radius: 2px;
                }

                .close-btn {
                    position: absolute;
                    top: 16px;
                    right: 16px;
                    background: rgba(255, 255, 255, 0.05);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    color: #aab4c8;
                    width: 36px;
                    height: 36px;
                    border-radius: 10px;
                    cursor: pointer;
                    font-size: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s;
                }

                .close-btn:hover {
                    background: rgba(255, 255, 255, 0.1);
                    transform: rotate(90deg);
                }

                .modal-header {
                    text-align: center;
                    margin-bottom: 28px;
                }

                .header-icon-wrapper {
                    display: flex;
                    justify-content: center;
                    margin-bottom: 12px;
                }

                .header-icon {
                    width: 52px;
                    height: 52px;
                    background: linear-gradient(135deg, #3d6fff, #7c4fff);
                    border-radius: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 8px 24px rgba(61, 111, 255, 0.25);
                }

                .modal-header h2 {
                    color: #e8edf5;
                    font-size: 22px;
                    font-weight: 600;
                    margin: 0 0 4px 0;
                }

                .modal-header .subtitle {
                    color: #6b7a96;
                    font-size: 14px;
                    margin: 0;
                }

                /* Plan Type */
                .plan-type-section {
                    margin-bottom: 24px;
                }

                .section-label {
                    color: #6b7a96;
                    font-size: 12px;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.08em;
                    display: block;
                    margin-bottom: 10px;
                }

                .required {
                    color: #ff6b6b;
                }

                .field-hint {
                    color: #6b7a96;
                    font-size: 11px;
                    font-weight: 400;
                    margin-left: 8px;
                }

                .input-hint {
                    color: #6b7a96;
                    font-size: 12px;
                    margin-top: 6px;
                    padding: 8px 12px;
                    background: rgba(255, 255, 255, 0.03);
                    border-radius: 8px;
                }

                .plan-type-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 10px;
                }

                .plan-type-card {
                    background: transparent;
                    border: 1.5px solid rgba(255, 255, 255, 0.08);
                    border-radius: 12px;
                    padding: 14px 16px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    transition: all 0.2s;
                    position: relative;
                }

                .plan-type-card:hover {
                    border-color: rgba(255, 255, 255, 0.2);
                    transform: translateY(-1px);
                }

                .plan-type-card.active {
                    border-color: var(--plan-color, #3d6fff);
                }

                .plan-type-icon {
                    font-size: 24px;
                    flex-shrink: 0;
                }

                .plan-type-info {
                    flex: 1;
                }

                .plan-type-name {
                    font-size: 14px;
                    font-weight: 500;
                    color: #e8edf5;
                    display: block;
                }

                .plan-type-badge {
                    font-size: 11px;
                    color: #6b7a96;
                }

                .plan-type-check {
                    flex-shrink: 0;
                }

                .info-banner {
                    padding: 16px 20px;
                    border-radius: 12px;
                    border: 1px solid;
                    display: flex;
                    gap: 14px;
                    margin-bottom: 24px;
                    align-items: flex-start;
                }

                .info-icon {
                    font-size: 22px;
                    flex-shrink: 0;
                }

                .info-content {
                    flex: 1;
                }

                .info-content strong {
                    display: block;
                    font-size: 14px;
                    margin-bottom: 4px;
                }

                .info-content p {
                    margin: 0;
                    font-size: 13px;
                    color: #aab4c8;
                    line-height: 1.5;
                }

                .free-note {
                    background: rgba(76, 175, 80, 0.1);
                    border: 1px solid rgba(76, 175, 80, 0.3);
                    border-radius: 12px;
                    padding: 16px 20px;
                    display: flex;
                    gap: 14px;
                    margin-bottom: 20px;
                    align-items: flex-start;
                }

                .free-note-icon {
                    font-size: 20px;
                }

                .free-note-content strong {
                    display: block;
                    color: #4CAF50;
                    font-size: 14px;
                    margin-bottom: 4px;
                }

                .free-note-content p {
                    margin: 0;
                    font-size: 13px;
                    color: #aab4c8;
                }

                .form-group {
                    margin-bottom: 20px;
                }

                .field-label {
                    color: #6b7a96;
                    font-size: 12px;
                    font-weight: 600;
                    display: block;
                    margin-bottom: 6px;
                }

                .input-wrapper {
                    position: relative;
                }

                .form-input {
                    width: 100%;
                    background: rgba(255, 255, 255, 0.04);
                    border: 1.5px solid rgba(255, 255, 255, 0.08);
                    border-radius: 10px;
                    color: #e8edf5;
                    padding: 12px 16px;
                    font-size: 14px;
                    transition: all 0.2s;
                    box-sizing: border-box;
                }

                .form-input:focus {
                    border-color: #3d6fff;
                    outline: none;
                    box-shadow: 0 0 0 3px rgba(61, 111, 255, 0.1);
                }

                .form-input.error {
                    border-color: #ff6b6b;
                }

                .form-input::placeholder {
                    color: rgba(107, 122, 150, 0.5);
                }

                .form-textarea {
                    resize: vertical;
                    min-height: 50px;
                    font-family: inherit;
                }

                .form-row {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 16px;
                }

                .error-message {
                    color: #ff6b6b;
                    font-size: 12px;
                    display: block;
                    margin-top: 4px;
                }

                /* Toggle */
                .toggle {
                    width: 40px;
                    height: 22px;
                    border-radius: 11px;
                    position: relative;
                    cursor: pointer;
                    transition: background 0.3s;
                    flex-shrink: 0;
                }

                .toggle.on {
                    background: #9C27B0;
                }

                .toggle.off {
                    background: rgba(255, 255, 255, 0.1);
                }

                .toggle-thumb {
                    width: 16px;
                    height: 16px;
                    background: white;
                    border-radius: 50%;
                    position: absolute;
                    top: 3px;
                    transition: all 0.3s;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
                }

                .toggle-thumb.on {
                    left: 21px;
                }

                .toggle-thumb.off {
                    left: 3px;
                }

                /* Upload */
                .upload-zone {
                    background: rgba(255, 255, 255, 0.03);
                    border: 2px dashed rgba(255, 255, 255, 0.1);
                    border-radius: 10px;
                    padding: 16px;
                    min-height: 80px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s;
                    cursor: pointer;
                }

                .upload-zone:hover {
                    border-color: rgba(61, 111, 255, 0.3);
                }

                .upload-zone.error {
                    border-color: #ff6b6b;
                }

                .upload-label {
                    cursor: pointer;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 6px;
                    width: 100%;
                }

                .upload-icon {
                    color: #6b7a96;
                }

                .upload-text {
                    text-align: center;
                }

                .upload-main {
                    color: #e8edf5;
                    font-size: 14px;
                    font-weight: 500;
                    display: block;
                }

                .upload-sub {
                    color: #6b7a96;
                    font-size: 12px;
                }

                .file-input {
                    display: none;
                }

                .image-preview-wrapper {
                    position: relative;
                    display: inline-block;
                }

                .image-preview {
                    width: 60px;
                    height: 60px;
                    object-fit: cover;
                    border-radius: 12px;
                    border: 2px solid rgba(255, 255, 255, 0.1);
                }

                .delete-image-btn {
                    position: absolute;
                    top: -8px;
                    right: -8px;
                    background: #ff6b6b;
                    border: none;
                    color: white;
                    width: 24px;
                    height: 24px;
                    border-radius: 50%;
                    cursor: pointer;
                    font-size: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: transform 0.2s;
                    box-shadow: 0 2px 8px rgba(255, 107, 107, 0.3);
                }

                .delete-image-btn:hover {
                    transform: scale(1.1);
                }

                /* Tag */
                .tag-input-wrapper {
                    background: rgba(255, 255, 255, 0.03);
                    border-radius: 12px;
                    padding: 16px;
                    border: 1.5px solid rgba(255, 255, 255, 0.06);
                    transition: all 0.2s;
                }

                .tag-input-wrapper:focus-within {
                    border-color: rgba(61, 111, 255, 0.3);
                    background: rgba(61, 111, 255, 0.04);
                }

                .tag-input-header {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    margin-bottom: 10px;
                }

                .tag-icon {
                    font-size: 18px;
                }

                .tag-label-text {
                    color: #6b7a96;
                    font-size: 13px;
                    font-weight: 500;
                }

                .tag-input {
                    background: transparent !important;
                    border: none !important;
                    padding: 6px 0 !important;
                    font-size: 14px !important;
                    color: #e8edf5 !important;
                    width: 100%;
                }

                .tag-input:focus {
                    box-shadow: none !important;
                }

                .tag-input::placeholder {
                    color: rgba(107, 122, 150, 0.4);
                }

                .tag-hint {
                    color: #6b7a96;
                    font-size: 12px;
                    margin-top: 8px;
                    opacity: 0.7;
                }

                /* Ambassador */
                .ambassador-fields {
                    border-top: 1px solid rgba(255, 255, 255, 0.06);
                    padding-top: 20px;
                    margin-top: 4px;
                }

                .ambassador-duration-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 8px;
                }

                .ambassador-duration-card {
                    background: rgba(255, 255, 255, 0.03);
                    border: 1.5px solid rgba(255, 255, 255, 0.06);
                    border-radius: 10px;
                    padding: 10px;
                    cursor: pointer;
                    text-align: center;
                    transition: all 0.2s;
                }

                .ambassador-duration-card:hover {
                    border-color: rgba(255, 255, 255, 0.15);
                    transform: translateY(-1px);
                }

                .ambassador-duration-card.active {
                    border-color: #9C27B0;
                    background: rgba(156, 39, 176, 0.08);
                }

                .duration-icon {
                    font-size: 18px;
                    display: block;
                    margin-bottom: 2px;
                }

                .duration-label {
                    color: #e8edf5;
                    font-size: 13px;
                    font-weight: 500;
                    display: block;
                }

                .duration-days {
                    color: #6b7a96;
                    font-size: 11px;
                }

                /* Pro */
                .pro-fields {
                    border-top: 1px solid rgba(255, 255, 255, 0.06);
                    padding-top: 20px;
                    margin-top: 4px;
                }

                .billing-period-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 8px;
                }

                .billing-period-card {
                    background: rgba(255, 255, 255, 0.03);
                    border: 1.5px solid rgba(255, 255, 255, 0.06);
                    border-radius: 10px;
                    padding: 10px;
                    cursor: pointer;
                    text-align: center;
                    transition: all 0.2s;
                }

                .billing-period-card:hover {
                    border-color: rgba(255, 255, 255, 0.15);
                    transform: translateY(-1px);
                }

                .billing-period-card.active {
                    border-color: #FF6B35;
                    background: rgba(255, 107, 53, 0.08);
                }

                .period-icon {
                    font-size: 18px;
                    display: block;
                    margin-bottom: 2px;
                }

                .period-label {
                    color: #e8edf5;
                    font-size: 13px;
                    font-weight: 500;
                    display: block;
                }

                .period-days {
                    color: #6b7a96;
                    font-size: 11px;
                }

                .price-input-wrapper {
                    position: relative;
                    display: flex;
                    align-items: center;
                }

                .currency-symbol {
                    position: absolute;
                    left: 14px;
                    color: #6b7a96;
                    font-size: 16px;
                    font-weight: 600;
                }

                .price-input {
                    padding-left: 32px !important;
                }

                .period-label-display {
                    position: absolute;
                    right: 14px;
                    color: #6b7a96;
                    font-size: 13px;
                }

                /* Achievements */
                .achievements-section {
                    border-top: 1px solid rgba(255, 255, 255, 0.06);
                    padding-top: 20px;
                    margin-top: 4px;
                }

                .achievement-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 12px;
                }

                .achievement-card {
                    background: rgba(255, 255, 255, 0.03);
                    border: 1.5px solid rgba(255, 255, 255, 0.06);
                    border-radius: 12px;
                    padding: 14px;
                    transition: all 0.2s;
                }

                .achievement-card.active {
                    border-color: rgba(156, 39, 176, 0.3);
                    background: rgba(156, 39, 176, 0.05);
                }

                .achievement-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .achievement-info {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }

                .achievement-icon {
                    font-size: 18px;
                }

                .achievement-label {
                    color: #e8edf5;
                    font-size: 13px;
                    font-weight: 500;
                }

                .achievement-fields {
                    margin-top: 12px;
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 10px;
                }

                .achievement-field {
                    grid-column: span 1;
                }

                .achievement-field.full-width {
                    grid-column: span 2;
                }

                .achievement-field-label {
                    color: #6b7a96;
                    font-size: 11px;
                    display: block;
                    margin-bottom: 4px;
                }

                .achievement-input {
                    width: 100%;
                    background: rgba(0, 0, 0, 0.3);
                    border: 1px solid rgba(255, 255, 255, 0.06);
                    border-radius: 8px;
                    color: #e8edf5;
                    padding: 8px 12px;
                    font-size: 13px;
                    transition: all 0.2s;
                    box-sizing: border-box;
                }

                .achievement-input:focus {
                    border-color: #9C27B0;
                    outline: none;
                }

                /* Submit */
                .submit-btn {
                    width: 100%;
                    background: linear-gradient(135deg, #3d6fff, #7c4fff);
                    color: white;
                    border: none;
                    border-radius: 12px;
                    padding: 14px;
                    font-size: 16px;
                    font-weight: 600;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    transition: all 0.2s;
                    margin-top: 8px;
                }

                .submit-btn:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 24px rgba(61, 111, 255, 0.3);
                }

                .submit-btn:active:not(:disabled) {
                    transform: scale(0.98);
                }

                .submit-btn:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }

                .spinner {
                    width: 18px;
                    height: 18px;
                    border: 2px solid rgba(255, 255, 255, 0.3);
                    border-top-color: white;
                    border-radius: 50%;
                    animation: spin 0.6s linear infinite;
                }

                @keyframes spin {
                    to { transform: rotate(360deg); }
                }

                @media (max-width: 768px) {
                    .modal-container {
                        padding: 20px;
                        max-width: 100%;
                        margin: 10px;
                    }

                    .plan-type-grid {
                        grid-template-columns: 1fr;
                    }

                    .form-row {
                        grid-template-columns: 1fr;
                    }

                    .billing-period-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }

                    .ambassador-duration-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }

                    .achievement-grid {
                        grid-template-columns: 1fr;
                    }

                    .achievement-fields {
                        grid-template-columns: 1fr;
                    }

                    .achievement-field.full-width {
                        grid-column: span 1;
                    }
                }

                @media (max-width: 480px) {
                    .modal-container {
                        padding: 16px;
                    }

                    .plan-type-card {
                        padding: 12px;
                    }

                    .billing-period-grid {
                        grid-template-columns: 1fr 1fr;
                    }

                    .ambassador-duration-grid {
                        grid-template-columns: 1fr 1fr;
                    }
                }
            `}</style>
        </div>
    );
};

export default CreatePlanModal;